import { SpeedInsights } from '@vercel/speed-insights/react';
import { useEventListener } from 'ahooks';
import 'focus-visible/dist/focus-visible';
import { Suspense, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { RouterProvider } from 'react-router';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
	getWithExpiry,
	setWithExpiry,
} from './components/ErrorBoundary/errorFallbackRenderer';
import { FullWidthLoadingSpinner } from './components/LoadingSpinner';
import { ReactPortalReusableProviders } from './providers';
import { router } from './router';
import { IS_LOCAL_OR_DEVELOPMENT } from './utils/envs';

// Error handling constants
const CHUNK_FAILED_KEY = 'chunk_failed';
const CHUNK_FAILED_EXPIRY_MS = 10000;
const DATABASE_CLOSED_ERROR_NAME = 'DatabaseClosedError';

/**
 * Handles database corruption errors by clearing all IndexedDB databases
 */
const handleDatabaseCorruption = async (): Promise<void> => {
	try {
		const databases: IDBDatabaseInfo[] = await window.indexedDB.databases();

		// Delete all databases to resolve corruption
		const deletePromises: Promise<void>[] = databases
			.filter((db: IDBDatabaseInfo) => db.name !== undefined)
			.map(
				(db: IDBDatabaseInfo) =>
					new Promise<void>((resolve, reject) => {
						const request = window.indexedDB.deleteDatabase(db.name!);
						request.onsuccess = () => resolve();
						request.onerror = () => reject(request.error);
					})
			);

		await Promise.all(deletePromises);
	} catch (error: unknown) {
		// Silently handle database clearing errors to prevent additional failures
	}
};

/**
 * Handles chunk loading failures by reloading the application
 */
const handleChunkLoadFailure = (): void => {
	const hasChunkFailed: string | null = getWithExpiry(CHUNK_FAILED_KEY);

	if (!hasChunkFailed) {
		setWithExpiry(CHUNK_FAILED_KEY, 'true', CHUNK_FAILED_EXPIRY_MS);
		window.location.reload();
	}
};

export function App(): JSX.Element {
	const handleUnhandledRejection = useCallback(
		async (event: PromiseRejectionEvent): Promise<void> => {
			const { reason } = event;

			if (
				reason &&
				typeof reason === 'object' &&
				'name' in reason &&
				reason.name === DATABASE_CLOSED_ERROR_NAME
			) {
				await handleDatabaseCorruption();
			}
		},
		[]
	);

	// Handle Vite chunk preload errors
	useEventListener('vite:preloadError', handleChunkLoadFailure);

	// Handle unhandled promise rejections
	useEventListener('unhandledrejection', handleUnhandledRejection);

	//  Mantine `Notifications` and `ModalsProvider` cannot be stored in the generic
	//  `Providers`, because it is reused in the outline editor and will duplicate
	//  calls to `modals.` and `notifications.`

	return (
		<ReactPortalReusableProviders>
			{!IS_LOCAL_OR_DEVELOPMENT && <SpeedInsights />}
			<ErrorBoundary>
				<Suspense fallback={<FullWidthLoadingSpinner />}>
					<Helmet titleTemplate="%s - Secoda" defaultTitle="Secoda" />
					<div id="main">
						<RouterProvider router={router} />
					</div>
				</Suspense>
			</ErrorBoundary>
		</ReactPortalReusableProviders>
	);
}
